import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BEAUTY_PRODUCTS } from '../../../beauty-shared/config/saasModules';
import { generateHairFluxPrompt, fetchHairFluxPromptBuildInfo } from '../utils/hairFluxPromptApi';
import './HairFluxPromptPage.css';

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export default function HairFluxPromptPage() {
  const fileRef = useRef(null);
  const [sourceImage, setSourceImage] = useState('');
  const [prompt, setPrompt] = useState('');
  const [wrapIdentity, setWrapIdentity] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resultImage, setResultImage] = useState('');
  const [meta, setMeta] = useState(null);
  const [rev, setRev] = useState('');

  useEffect(() => {
    fetchHairFluxPromptBuildInfo()
      .then((d) => setRev(d?.rev || ''))
      .catch(() => {});
  }, []);

  const onPickFile = async (file) => {
    if (!file) return;
    setError('');
    try {
      const url = await readFileAsDataUrl(file);
      setSourceImage(url);
      setResultImage('');
      setMeta(null);
    } catch (e) {
      setError(e.message || 'Upload failed');
    }
  };

  const onGenerate = useCallback(async () => {
    if (!sourceImage) {
      setError('Upload a photo first.');
      return;
    }
    if (!prompt.trim()) {
      setError('Enter a hairstyle prompt.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const data = await generateHairFluxPrompt({
        image: sourceImage,
        prompt: prompt.trim(),
        wrapIdentity,
      });
      setResultImage(data.image || '');
      setMeta(data.meta || null);
    } catch (e) {
      setError(e.message || 'Generation failed');
      setResultImage('');
      setMeta(null);
    } finally {
      setBusy(false);
    }
  }, [sourceImage, prompt, wrapIdentity]);

  const hub = BEAUTY_PRODUCTS.hairFluxPrompt?.naturalsHubPath || '/nyafaz-ep/salon/test';

  return (
    <div className="hfp-page">
      {busy ? <div className="hfp-busy" role="status">Running FLUX (prompt only)…</div> : null}
      <header className="hfp-header">
        <div>
          <Link className="hfp-back" to={hub}>← Test studios</Link>
          <h1>Hair FLUX Prompt</h1>
        </div>
        <span className="hfp-tag">No masks · prompt only{rev ? ` · ${rev}` : ''}</span>
      </header>

      <div className="hfp-layout">
        <aside className="hfp-panel">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => onPickFile(e.target.files?.[0])}
          />
          <div
            className="hfp-drop"
            role="button"
            tabIndex={0}
            onClick={() => fileRef.current?.click()}
            onKeyDown={(e) => e.key === 'Enter' && fileRef.current?.click()}
          >
            {sourceImage ? (
              <img src={sourceImage} alt="Guest upload" />
            ) : (
              <span>Click to upload guest photo</span>
            )}
          </div>

          <label className="hfp-label">
            <span>Hairstyle prompt</span>
            <textarea
              value={prompt}
              disabled={busy}
              placeholder="e.g. Korean hush cut with soft fringe, collarbone length, keep natural black hair color"
              onChange={(e) => setPrompt(e.target.value)}
            />
          </label>

          <label className="hfp-check">
            <input
              type="checkbox"
              checked={wrapIdentity}
              disabled={busy}
              onChange={(e) => setWrapIdentity(e.target.checked)}
            />
            Wrap prompt with identity / preserve face &amp; background
          </label>

          <button type="button" className="hfp-btn" disabled={busy} onClick={onGenerate}>
            Generate
          </button>

          {error ? <div className="hfp-error">{error}</div> : null}

          {meta ? (
            <div className="hfp-meta">
              delivery: {meta.delivery || '—'}
              <br />
              steps: {meta.steps} · guidance: {meta.guidance}
              {meta.lab_ms != null ? ` · ${meta.lab_ms}ms` : ''}
            </div>
          ) : null}
        </aside>

        <section className="hfp-compare">
          <div className="hfp-frame">
            <div className="hfp-frame-label">Original</div>
            {sourceImage ? (
              <img src={sourceImage} alt="Original" />
            ) : (
              <div className="hfp-placeholder">Upload to compare</div>
            )}
          </div>
          <div className="hfp-frame">
            <div className="hfp-frame-label">FLUX result</div>
            {resultImage ? (
              <img src={resultImage} alt="Generated" />
            ) : (
              <div className="hfp-placeholder">Result appears here</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
